import { Card, Separator, Text } from "@dotlocker/ui";
import { ModeToggle, ThemeVariantSelect } from "@dotlocker/ui-shared";
import { createRoute, Link } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";
import { SettingsLayoutRoute } from "./settings-layout";
import type { FileRecord, GrantRecord } from "~webui/lib/api";
import { useSession } from "~webui/lib/session";

export const SettingsRoute = createRoute({
  getParentRoute: () => SettingsLayoutRoute,
  path: "/settings",
  component: SettingsPage,
});

interface ProjectRow {
  org: string;
  name: string;
  files: number;
  runtimes: Set<string>;
  access: Set<string>;
}

function SettingsPage() {
  const { api, me, org, server } = useSession();
  const organizations = me?.orgs?.length ? me.orgs : [org];
  const [files, setFiles] = useState<FileRecord[]>([]);
  const [inventoryError, setInventoryError] = useState("");

  useEffect(() => {
    let active = true;
    setInventoryError("");
    Promise.all(organizations.map((name) => api.files(name).then((result) => result.files)))
      .then((values) => {
        if (active) setFiles(values.flat());
      })
      .catch((reason) => {
        if (active) setInventoryError(`Could not load project inventory: ${String(reason)}`);
      });
    return () => {
      active = false;
    };
  }, [api, organizations.join("\0")]);

  const projects = useMemo(() => {
    const rows = new Map<string, ProjectRow>();
    const ensure = (organization: string, name: string) => {
      const key = `${organization}/${name}`;
      const row = rows.get(key) ?? {
        org: organization,
        name,
        files: 0,
        runtimes: new Set<string>(),
        access: new Set<string>(),
      };
      rows.set(key, row);
      return row;
    };
    for (const file of files) {
      const row = ensure(file.org, file.repo);
      row.files += 1;
      row.runtimes.add(file.runtime);
    }
    for (const grant of (me?.grants ?? []) as GrantRecord[]) {
      if (!grant.repo) continue;
      ensure(grant.org, grant.repo).access.add(grant.access);
    }
    return [...rows.values()].sort((a, b) =>
      `${a.org}/${a.name}`.localeCompare(`${b.org}/${b.name}`),
    );
  }, [files, me?.grants]);

  return (
    <div className="grid gap-5">
      <Card className="overflow-hidden p-0">
        <div className="border-b border-[var(--pl-line)] px-5 py-4">
          <Text as="h2" variant="title">
            Organizations
          </Text>
          <Text className="mt-1 text-[var(--pl-muted)]">
            Keyname organizations available to your signed-in identity.
          </Text>
        </div>
        <div className="overflow-x-auto">
          <table className="mt-0 w-full min-w-[560px] rounded-none border-0">
            <thead>
              <tr>
                <th>Organization</th>
                <th>Projects</th>
                <th>Scope</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {organizations.map((name) => (
                <tr key={name}>
                  <td className="font-semibold text-[var(--pl-text)]">{name}</td>
                  <td>{projects.filter((project) => project.org === name).length}</td>
                  <td>
                    <code>{name}/**</code>
                  </td>
                  <td>
                    <span className="pill">{name === org ? "Current" : "Available"}</span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      <Card className="overflow-hidden p-0">
        <div className="flex flex-wrap items-start justify-between gap-4 border-b border-[var(--pl-line)] px-5 py-4">
          <div>
            <Text as="h2" variant="title">
              Projects
            </Text>
            <Text className="mt-1 text-[var(--pl-muted)]">
              Repository projects visible through your organization grants.
            </Text>
          </div>
          <Link
            to="/repos"
            className="text-xs font-semibold text-[var(--pl-primary)] no-underline hover:underline"
          >
            Manage projects →
          </Link>
        </div>
        {inventoryError ? (
          <p
            role="alert"
            className="m-0 border-b border-[var(--pl-line)] px-5 py-3 text-xs text-[var(--pl-danger)]"
          >
            {inventoryError}
          </p>
        ) : null}
        <div className="overflow-x-auto">
          <table className="mt-0 w-full min-w-[680px] rounded-none border-0">
            <thead>
              <tr>
                <th>Project</th>
                <th>Organization</th>
                <th>Runtimes</th>
                <th>Files</th>
                <th>Access</th>
              </tr>
            </thead>
            <tbody>
              {projects.map((project) => (
                <tr key={`${project.org}/${project.name}`}>
                  <td className="font-semibold text-[var(--pl-text)]">{project.name}</td>
                  <td>{project.org}</td>
                  <td>
                    {[...project.runtimes].sort().map((runtime) => (
                      <span key={runtime} className="pill">
                        {runtime}
                      </span>
                    ))}
                    {!project.runtimes.size ? "—" : null}
                  </td>
                  <td>{project.files}</td>
                  <td className="capitalize">
                    {[...project.access].sort().join(", ") || "Inherited"}
                  </td>
                </tr>
              ))}
              {!projects.length ? (
                <tr>
                  <td colSpan={5} className="py-10 text-center text-[var(--pl-muted)]">
                    No projects are visible yet.
                  </td>
                </tr>
              ) : null}
            </tbody>
          </table>
        </div>
      </Card>

      <Card>
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <Text as="h2" variant="title">
              Personal API access
            </Text>
            <Text className="mt-1 text-[var(--pl-muted)]">
              API keys are linked to your Keyname identity and remain constrained by current
              organization and project access.
            </Text>
          </div>
          <Link
            to="/settings/api-keys"
            className="rounded-[var(--pl-radius-xs)] border border-[var(--pl-primary)] bg-[var(--pl-primary)] px-3.5 py-2 text-sm font-semibold text-[var(--pl-bg)] no-underline"
          >
            Manage API keys
          </Link>
        </div>
      </Card>

      <Card>
        <Text as="h2" variant="title">
          Appearance
        </Text>
        <Text className="mt-1 text-[var(--pl-muted)]">
          Choose how the dot.locker dashboard looks on this device.
        </Text>
        <Separator className="my-5" />
        <div className="flex flex-wrap items-center justify-between gap-5">
          <div>
            <Text className="font-semibold">Theme variant</Text>
            <Text className="mt-1 text-[var(--pl-muted)]">Select a dashboard color palette.</Text>
          </div>
          <ThemeVariantSelect />
        </div>
        <Separator className="my-5" />
        <div className="flex flex-wrap items-center justify-between gap-5">
          <div>
            <Text className="font-semibold">Color mode</Text>
            <Text className="mt-1 text-[var(--pl-muted)]">Switch between light and dark mode.</Text>
          </div>
          <ModeToggle />
        </div>
      </Card>

      <Card>
        <Text as="h2" variant="title">
          Session and identity
        </Text>
        <Text className="mt-1 text-[var(--pl-muted)]">
          Authentication, passkeys, MFA, and recovery are managed by Keyname.
        </Text>
        <Separator className="my-5" />
        <div className="grid gap-5 sm:grid-cols-2">
          <div>
            <Text variant="label" className="text-[var(--pl-subtle)]">
              Signed in as
            </Text>
            <Text className="mt-1 font-semibold">
              {me?.identityEmail ?? me?.token.userEmail ?? "Loading…"}
            </Text>
          </div>
          <div>
            <Text variant="label" className="text-[var(--pl-subtle)]">
              API endpoint
            </Text>
            <Text className="mt-1 break-all font-mono text-xs">{server || location.origin}</Text>
          </div>
        </div>
      </Card>
    </div>
  );
}
